import { FC } from 'react';
import {
  Box,
  BoxProps,
  Menu,
  MenuList,
  Text,
  useDisclosure,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  useTheme,
} from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { rgba } from 'polished';
import { ReactComponent as SettingsIcon } from 'assets/svgs/gear.svg';
import CustomSelect from 'components/Select/CustomSelect';
import { translateProjectsAndPortfolios } from 'modules/projects/projectsSlice';
import { updateLanguage } from 'modules/user/userSlice';
import ListItem from '../shared/ListItem';
import MenuButton from '../shared/MenuButton';
import { getLanguages } from '../shared/getLanguages';
import { StyledAccordionIcon, MobileListItem } from '../shared/StyledItems';

interface Props extends BoxProps {
  isMobile?: boolean;
  languages: string[] | undefined;
}

const highlightPadding = 16;

const SettingsMenu: FC<Props> = ({ isMobile = false, languages, ...props }) => {
  const { i18n, t } = useTranslation('common');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const dispatch = useDispatch();
  const { colors } = useTheme();
  const languageDefaultValue = getLanguages(languages).find(item => item.value === i18n.language)?.label ?? '';

  const onLanguageSelect = (lang: string): void => {
    i18n.changeLanguage(lang);
    dispatch(translateProjectsAndPortfolios(lang));
    dispatch(updateLanguage(lang));
  };

  const SelectLanguage = () => (
    <CustomSelect
      options={getLanguages(languages)}
      defaultValue={languageDefaultValue ? { label: languageDefaultValue, value: i18n.language } : null}
      borderRadius="8px"
      variant="custom"
      width={isMobile ? '150px' : '170px'}
      height="43px"
      onChange={e => {
        const option = e === null ? undefined : (e as Option);
        if (option) {
          onLanguageSelect(option.value);
          onClose();
        }
      }}
    />
  );

  if (isMobile) {
    return (
      <Accordion allowToggle>
        <AccordionItem border="none">
          <AccordionButton d="flex" justifyContent="space-between" alignItems="center" p="0 24px" sx={{ _hover: { background: 'none' } }}>
            {t('profileMenu.settings')}
            <StyledAccordionIcon iconColor={colors.secondary[500]} />
          </AccordionButton>
          <AccordionPanel p="0">
            <MobileListItem borderTop="1px solid" borderColor="secondary.300">
              <Text as="span" d="flex" alignItems="center" height="43px">
                {t('profileMenu.language')}
              </Text>
              <Box pos="absolute" right="24px">
                <SelectLanguage />
              </Box>
            </MobileListItem>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    );
  }

  return (
    <Box {...props}>
      <Menu autoSelect={false} placement="bottom-end" isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
        <MenuButton rightIcon isOpen={isOpen} openedMenuHighlightPadding={highlightPadding}>
          <SettingsIcon />
        </MenuButton>
        <MenuList
          p="20px 24px"
          w="350px"
          mr={`-${highlightPadding}px`}
          mt="25px"
          boxShadow={`0 4px 10px ${rgba('black', 0.1)}`}
          borderColor="transparent"
        >
          <ListItem>
            <Text as="span">{t('profileMenu.language')}</Text>
            <SelectLanguage />
          </ListItem>
        </MenuList>
      </Menu>
    </Box>
  );
};

export default SettingsMenu;
